import { useState, useMemo } from 'react'
import { MetricCard, SectionHeader, Toggle, NumberField, SelectField, AgronomistNote, Tooltip } from '@/components/ui/CalcUI'

type AreaUnit = 'Acres' | 'Hectares'

type SpacingSystem = 'traditional' | 'semi' | 'high' | 'uhd' | 'custom'

interface SpacingPreset {
  id: SpacingSystem
  label: string
  row: number
  tree: number
  desc: string
}

const PRESETS: SpacingPreset[] = [
  { id: 'traditional', label: 'Traditional / Low-Density', row: 10, tree: 10, desc: '10m × 10m' },
  { id: 'semi', label: 'Semi-Intensive', row: 8, tree: 6, desc: '8m × 6m' },
  { id: 'high', label: 'High-Density', row: 5, tree: 5, desc: '5m × 5m' },
  { id: 'uhd', label: 'Ultra-High Density (UHD)', row: 3, tree: 2, desc: '3m × 2m' },
  { id: 'custom', label: 'Custom Spacing', row: 4, tree: 4, desc: 'Custom' },
]

const PRESET_COLORS: Record<SpacingSystem, { bg: string; border: string; text: string }> = {
  traditional: { bg: '#f0fdf4', border: '#86efac', text: '#14532d' },
  semi: { bg: '#f0fdf4', border: '#4ade80', text: '#166534' },
  high: { bg: '#ecfccb', border: '#a3e635', text: '#365314' },
  uhd: { bg: '#fef9c3', border: '#fde047', text: '#713f12' },
  custom: { bg: '#fef3c7', border: '#fbbf24', text: '#92400e' },
}

export default function OrchardSpacing() {
  const [areaUnit, setAreaUnit] = useState<AreaUnit>('Hectares')
  const [totalArea, setTotalArea] = useState(1)
  const [selectedSystem, setSelectedSystem] = useState<SpacingSystem>('high')
  const [customRow, setCustomRow] = useState(4)
  const [customTree, setCustomTree] = useState(4)

  const activePreset = PRESETS.find((p) => p.id === selectedSystem)!

  const rowSpacing = selectedSystem === 'custom' ? customRow : activePreset.row
  const treeSpacing = selectedSystem === 'custom' ? customTree : activePreset.tree

  const results = useMemo(() => {
    const sqm = areaUnit === 'Acres' ? totalArea * 4046.86 : totalArea * 10000
    const hectares = sqm / 10000
    const totalTrees = rowSpacing > 0 && treeSpacing > 0 ? Math.floor(sqm / (rowSpacing * treeSpacing)) : 0
    const beeHives = areaUnit === 'Acres' ? Math.ceil(totalArea) : Math.ceil(hectares * 2)
    return { totalTrees, beeHives, sqm, hectares }
  }, [areaUnit, totalArea, rowSpacing, treeSpacing])

  // Mini grid visualization
  const gridCols = Math.min(Math.ceil(Math.sqrt(results.totalTrees)), 20)
  const gridRows = Math.min(Math.ceil(results.totalTrees / gridCols), 20)
  const displayTrees = gridCols * gridRows
  const isDense = selectedSystem === 'uhd'

  return (
    <div>
      <SectionHeader
        badge="Calculator 1"
        h2="Mango Orchard Spacing & Population Optimizer"
        description="Calculate tree population, bee hives needed, and visualize plantation density for any orchard area."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <div
          className="rounded-3xl border p-6"
          style={{ background: '#fff', borderColor: '#e8d9b0' }}
        >
          <h3 className="text-base font-bold mb-4" style={{ color: '#44403c' }}>📐 Orchard Parameters</h3>

          {/* Area Unit Toggle */}
          <div className="mb-4">
            <label className="text-sm font-semibold block mb-1.5 flex items-center gap-1" style={{ color: '#44403c' }}>
              Area Unit
              <Tooltip content="Acres are common in the US/India. Hectares are the international FAO standard for orchard planning." title="Area Units" />
            </label>
            <Toggle
              options={['Acres', 'Hectares']}
              value={areaUnit}
              onChange={(v) => setAreaUnit(v as AreaUnit)}
            />
          </div>

          <NumberField
            label="Total Area"
            value={totalArea}
            min={0.1}
            step={0.1}
            unit={areaUnit === 'Acres' ? 'ac' : 'ha'}
            onChange={setTotalArea}
            tooltip="Enter your total cultivable land area. The calculator converts to square meters internally."
          />

          {/* Plantation System */}
          <div className="mb-4">
            <label className="text-sm font-semibold block mb-2" style={{ color: '#44403c' }}>
              Plantation System
              <Tooltip
                content="Density determines canopy competition and management requirements. UHD systems require intensive pruning and fertigation."
                title="Plantation Density"
              />
            </label>
            <div className="space-y-2">
              {PRESETS.map((p) => {
                const c = PRESET_COLORS[p.id]
                const isActive = selectedSystem === p.id
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setSelectedSystem(p.id)}
                    className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl border text-sm font-medium transition-all duration-150"
                    style={
                      isActive
                        ? { background: c.bg, borderColor: c.border, color: c.text }
                        : { background: '#fafaf9', borderColor: '#e8d9b0', color: '#57534e' }
                    }
                  >
                    <span>{p.label}</span>
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded-full"
                      style={isActive ? { background: c.border + '50', color: c.text } : { background: '#f5f5f4', color: '#a8a29e' }}
                    >
                      {p.desc}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          {selectedSystem === 'custom' && (
            <div className="grid grid-cols-2 gap-3 mt-2 p-4 rounded-2xl border" style={{ background: '#fef9c3', borderColor: '#fde68a' }}>
              <NumberField
                label="Row Spacing"
                value={customRow}
                min={1}
                step={0.5}
                unit="m"
                onChange={setCustomRow}
              />
              <NumberField
                label="Tree Spacing"
                value={customTree}
                min={1}
                step={0.5}
                unit="m"
                onChange={setCustomTree}
              />
            </div>
          )}
        </div>

        {/* Results */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3">
            <MetricCard
              title="Total Tree Population"
              value={results.totalTrees.toLocaleString()}
              sub={`for ${totalArea} ${areaUnit} at ${rowSpacing}m × ${treeSpacing}m spacing`}
              icon="🌳"
              accent="#16a34a"
            />
            <MetricCard
              title="Recommended Grid"
              value={`${rowSpacing}m × ${treeSpacing}m`}
              sub={`${(10000 / (rowSpacing * treeSpacing)).toFixed(0)} trees / hectare`}
              icon="📐"
              accent="#0284c7"
            />
            <MetricCard
              title="Pollinator Bee Hives"
              value={results.beeHives}
              sub="Hives recommended for optimal pollination"
              icon="🐝"
              accent="#d97706"
            />
          </div>

          {/* Plantation grid visualization */}
          <div
            className="rounded-3xl border p-5"
            style={{ background: '#fff', borderColor: '#e8d9b0' }}
          >
            <h3 className="text-sm font-bold mb-3" style={{ color: '#44403c' }}>
              🗺️ Density Visualization
              <span className="ml-2 text-xs font-normal" style={{ color: '#a8a29e' }}>
                (showing up to 400 trees)
              </span>
            </h3>
            <div
              className="p-3 rounded-2xl overflow-auto"
              style={{ background: '#f0fdf4', border: '1px dashed #86efac', maxHeight: '200px' }}
            >
              <div
                className="grid gap-0.5 mx-auto"
                style={{
                  gridTemplateColumns: `repeat(${gridCols}, ${isDense ? '10px' : '14px'})`,
                  width: 'fit-content',
                }}
              >
                {Array.from({ length: displayTrees }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-full"
                    style={{
                      width: isDense ? 8 : 12,
                      height: isDense ? 8 : 12,
                      background: i < results.totalTrees ? '#16a34a' : '#e8d9b0',
                      opacity: 0.85,
                    }}
                  />
                ))}
              </div>
              {results.totalTrees > displayTrees && (
                <p className="text-center text-xs mt-2" style={{ color: '#64748b' }}>
                  + {(results.totalTrees - displayTrees).toLocaleString()} more trees
                </p>
              )}
            </div>
          </div>

          <AgronomistNote
            text={`For ${selectedSystem === 'uhd' ? 'UHD systems' : 'high-density plantations'}, maintain regular canopy management. Trees/ha: ${(10000 / (rowSpacing * treeSpacing)).toFixed(0)}. Formula: Total Trees = Area (m²) ÷ (Row × Tree Spacing).`}
            type="info"
          />
        </div>
      </div>
    </div>
  )
}
